import { createContext, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import supabase from "../supabase";
import LoadingPage from "../pages/LoadingPage";
import type { Session } from "@supabase/supabase-js";
import type { Database } from '../../database.types';

type Profile = Database['public']['Tables']['profiles']['Row'] | null;
type Friend = Database['public']['Tables']['friends']['Row'];


interface SessionContextType {
  session: Session | null;
  profile: Profile;
  friends: Friend[] | null;
  settings: null;
  isLoading: boolean;
  dataLoaded: boolean;
  setProfile: Dispatch<SetStateAction<Profile>>;
  setDataLoaded: Dispatch<SetStateAction<boolean>>;
  updateProfileOptimistic: (tempProfile: Partial<Profile>) => void;
  confirmUpdate: (serverProfile: Profile) => void;
  rollbackUpdate: (originalProfile: Profile) => void;
  refreshProfile: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType>({
  session: null,
  profile: null,
  friends: null,
  settings: null,
  isLoading: true,
  dataLoaded: false,
  setProfile: () => {},
  setDataLoaded: () => {},
  updateProfileOptimistic: () => {},
  confirmUpdate: () => {},
  rollbackUpdate: () => {},
  refreshProfile: async () => {},
});

export { SessionContext };

type Props = { children: React.ReactNode };

export const SessionProvider = ({ children }: Props) => {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile>(null);
  const [friends, setFriends] = useState<Friend[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setIsLoading(false);
      }
    );
    return () => subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session?.user?.id) return;
    
    // Add session status check
    const isFullyAuthenticated = (
      session.user.aud === 'authenticated' && 
      session.user.confirmed_at !== null
    );
    
    if (!isFullyAuthenticated) {
      console.log('Skipping data load - session not fully authenticated');
      return;
    }

    const userId = session.user.id;
    let profileSub: ReturnType<typeof supabase.channel>;

    const loadDataAndSubscribe = async () => {
      try {
        // Fetch initial data
        // Use maybeSingle() to handle missing profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, created_at, updated_at, avatar_url, description, phone_number, username, display_name, app_role, age_verified, age_verified_at, age_verification_method')
          .eq('id', userId)
          .maybeSingle();
          
        if (profileError) {
          console.error('Profile fetch error:', profileError);
        }

        const { data: friendsData } = await supabase
          .from('friends')
          .select('*')
          .eq('user_id', userId);
        
        setProfile(profileData);
        setFriends(friendsData);
        setDataLoaded(true);

        // Profile changes subscription
        profileSub = supabase.channel('profile-changes')
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${userId}`
          }, (payload) => {
            setProfile(payload.new as Profile);
          })
          .subscribe();

      } catch (error) {
        console.error('Initial data load error:', error);
        setProfile(null);
        setFriends(null);
      }
    };

    loadDataAndSubscribe();

    return () => {
      if (profileSub) {
        supabase.removeChannel(profileSub);
      }
    };
  }, [session?.user?.id, session?.user?.aud, session?.user?.confirmed_at]);

  return (
    <SessionContext.Provider value={{
      session,
      profile,
      friends,
      settings: null,
      isLoading,
      dataLoaded,
      setProfile,
      setDataLoaded,
      updateProfileOptimistic: async (tempProfile) => {
        const userId = session?.user?.id;
        if (!userId || !profile) return;

        const originalProfile = profile;
        try {
          setProfile(prev => prev ? { ...prev, ...tempProfile } : null);

          const { error } = await supabase
            .from('profiles')
            .update(tempProfile)
            .eq('id', userId);

          if (error) throw error;
        } catch (error) {
          console.error('Profile update failed:', error);
          setProfile(originalProfile);
        }
      },
      refreshProfile: async () => {
        if (!session?.user?.id) return;
        
        try {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();
          
          if (data) setProfile(data);
        } catch (error) {
          console.error('Profile refresh failed:', error);
        }
      },
      confirmUpdate: setProfile,
      rollbackUpdate: setProfile
    }}>
      {isLoading ? <LoadingPage /> : children}
    </SessionContext.Provider>
  );
};
